import { useEffect, useState } from "react";
import { msg } from "./i18n";

const formatters = new Map<string, Intl.DateTimeFormat>();

/**
 * Date and time in the member's language, with the time zone named (4b step 3b): an unlock time
 * is exact to a few seconds, and a capsule may be read in another zone than it was written in.
 */
export function formatTime(ms: number): string {
  const locale = msg().locale;
  let f = formatters.get(locale);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
    formatters.set(locale, f);
  }
  return f.format(new Date(ms));
}

const shortFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * The same moment in a list row: day, month, hour and minute, the year only when it is not this
 * one. The full form (seconds, time zone) belongs in the row's title.
 */
export function formatTimeShort(ms: number): string {
  const locale = msg().locale;
  const d = new Date(ms);
  const thisYear = d.getFullYear() === new Date().getFullYear();
  const key = `${locale}|${thisYear ? "y" : "n"}`;
  let f = shortFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...(thisYear ? {} : { year: "numeric" }), month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    shortFormatters.set(key, f);
  }
  return f.format(d);
}

/** A calendar day in the member's language, for "since" dates. */
export const formatDay = (ms: number) => new Date(ms).toLocaleDateString(msg().locale, { year: "numeric", month: "short", day: "numeric" });

/** A whole number in the member's language (thousands separators). */
export const formatNumber = (n: number) => n.toLocaleString(msg().locale);

/** "3 d 4 h", "12 min 5 s": two most significant units, enough for a countdown. */
export function formatDuration(ms: number): string {
  const u = msg().time.units;
  const s = Math.max(0, Math.ceil(ms / 1000));
  const parts: [number, string][] = [
    [Math.floor(s / 31_536_000), u.y],
    [Math.floor((s % 31_536_000) / 86_400), u.d],
    [Math.floor((s % 86_400) / 3600), u.h],
    [Math.floor((s % 3600) / 60), u.min],
    [s % 60, u.s],
  ];
  const first = parts.findIndex(([n]) => n > 0);
  if (first === -1) return `0 ${u.s}`;
  return parts
    .slice(first, first + 2)
    .filter(([n]) => n > 0)
    .map(([n, unit]) => `${n} ${unit}`)
    .join(" ");
}

/** Countdown with seconds visible even when the opening is several days away. */
export function formatCountdown(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  const days = Math.floor(seconds / 86400);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${days ? `${days} ${msg().time.units.d} ` : ""}${pad(Math.floor(seconds % 86400 / 3600))}:${pad(Math.floor(seconds % 3600 / 60))}:${pad(seconds % 60)}`;
}

/** Calm list summary; the exact date and seconds remain in the message details. */
export function formatCountdownShort(ms: number): string {
  const minute = msg().time.units.min;
  if (ms <= 0) return `0 ${minute}`;
  if (ms < 60_000) return `< 1 ${minute}`;
  return formatDuration(Math.floor(ms / 60_000) * 60_000);
}

/** Current time, re-rendering every `everyMs`. */
export function useNow(everyMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}
